import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { Button, Card, DatePicker, Input, Select, Table, Tabs, Tag, Typography } from "antd";
import SheetDrawer from "../../components/sheet/SheetDrawer";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  exportSearchAnalytics,
  fetchAggregatedSearches,
  fetchSearchEvents,
} from "../../features/searchAnalytics/searchAnalyticsThunk";
import {
  resetFilters,
  setActiveTab,
  setFilters,
  setPage,
  setSort,
} from "../../features/searchAnalytics/searchAnalytics";
import { buildDisplayFromSearchData, formatSearchText } from "./searchAnalyticsUtils";
import "./SearchAnalytics.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { Title, Text } = Typography;

const PRODUCT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "flights", label: "Flights" },
  { value: "insurance", label: "Insurance" },
  { value: "events", label: "Events" },
];

const USER_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "guest", label: "Guest" },
  { value: "authenticated", label: "Authenticated" },
];

const DATE_PRESET_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last30", label: "Last 30 Days" },
  { value: "custom", label: "Custom" },
];

const toDateString = (value) => {
  if (!value) return null;
  if (value.format) return value.format("YYYY-MM-DD");
  return value;
};

const getDateRangeFromPreset = (preset) => {
  const end = moment().endOf("day");
  const start = moment().startOf("day");

  switch (preset) {
    case "today":
      return [start, end];
    case "yesterday":
      return [moment().subtract(1, "day").startOf("day"), moment().subtract(1, "day").endOf("day")];
    case "last7":
      return [moment().subtract(6, "days").startOf("day"), end];
    case "last30":
      return [moment().subtract(29, "days").startOf("day"), end];
    default:
      return [null, null];
  }
};

const SearchAnalytics = () => {
  const dispatch = useDispatch();
  const { admin } = useSelector((state) => state.auth);
  const { activeTab, filters, page, pageSize, sort, exportLoading, aggregated, raw } = useSelector((state) => state.searchAnalytics);
  const [selectedRow, setSelectedRow] = useState(null);
  const [customRange, setCustomRange] = useState([]);

  const queryParams = useMemo(() => {
    const effectiveFrom = filters.from || (filters.datePreset !== "custom" ? getDateRangeFromPreset(filters.datePreset)?.[0]?.format("YYYY-MM-DD") : null);
    const effectiveTo = filters.to || (filters.datePreset !== "custom" ? getDateRangeFromPreset(filters.datePreset)?.[1]?.format("YYYY-MM-DD") : null);
    return {
      product: filters.product !== "all" ? filters.product : undefined,
      userType: filters.userType !== "all" ? filters.userType : undefined,
      from: effectiveFrom || undefined,
      to: effectiveTo || undefined,
      includeBots: filters.includeBots ? true : undefined,
      search: filters.search || undefined,
      page,
      pageSize,
      sortBy: sort.field,
      sortOrder: sort.order,
    };
  }, [filters, page, pageSize, sort]);

  const loadData = useCallback(() => {
    if (activeTab === "aggregated") {
      dispatch(fetchAggregatedSearches(queryParams));
      return;
    }
    dispatch(fetchSearchEvents(queryParams));
  }, [activeTab, dispatch, queryParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleDatePresetChange = (value) => {
    if (value === "custom") {
      dispatch(setFilters({ datePreset: value, from: null, to: null }));
      return;
    }

    const [from, to] = getDateRangeFromPreset(value);
    dispatch(setFilters({
      datePreset: value,
      from: from ? from.format("YYYY-MM-DD") : null,
      to: to ? to.format("YYYY-MM-DD") : null,
    }));
  };

  const handleCustomRangeChange = (values) => {
    setCustomRange(values || []);
    dispatch(setFilters({
      datePreset: "custom",
      from: toDateString(values?.[0]),
      to: toDateString(values?.[1]),
    }));
  };

  const handleSort = (columnKey, direction) => {
    dispatch(setSort({ field: columnKey, order: direction }));
  };

  const handleExport = () => {
    dispatch(exportSearchAnalytics({
      ...queryParams,
      page: undefined,
      pageSize: undefined,
      sortBy: undefined,
      sortOrder: undefined,
    }));
  };

  const aggregatedColumns = [
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (value) => <Tag>{value || "Unknown"}</Tag>,
      sorter: true,
    },
    {
      title: "Search",
      dataIndex: "searchData",
      key: "searchData",
      render: (_, record) => buildDisplayFromSearchData(record.product, record.searchData || record.search || record.criteria),
    },
    {
      title: "Occurrences",
      dataIndex: "occurrenceCount",
      key: "occurrenceCount",
      sorter: true,
      render: (value) => value ?? 0,
    },
    {
      title: "Zero Results",
      dataIndex: "zeroResultCount",
      key: "zeroResultCount",
      render: (value) => (
        <Tag className={Number(value) > 0 ? "zero-result-tag" : ""}>{value ?? 0}</Tag>
      ),
    },
    {
      title: "Guest",
      dataIndex: "guestCount",
      key: "guestCount",
      render: (value) => value ?? 0,
    },
    {
      title: "Authenticated",
      dataIndex: "authenticatedCount",
      key: "authenticatedCount",
      render: (value) => value ?? 0,
    },
    {
      title: "First Searched",
      dataIndex: "firstSearchedAt",
      key: "firstSearchedAt",
      render: (value) => value ? moment(value).format("DD MMM YYYY") : "-",
    },
    {
      title: "Last Searched",
      dataIndex: "lastSearchedAt",
      key: "lastSearchedAt",
      render: (value) => value ? moment(value).format("DD MMM YYYY") : "-",
    },
  ];

  const rawColumns = [
    {
      title: "Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => value ? moment(value).format("DD MMM YYYY, HH:mm") : "-",
    },
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (value) => <Tag>{value || "Unknown"}</Tag>,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (value) => value?.email || value?.name || value || "Guest",
    },
    {
      title: "Search",
      dataIndex: "search",
      key: "search",
      render: (_, record) => formatSearchText(record),
    },
    {
      title: "Results",
      dataIndex: "resultsCount",
      key: "resultsCount",
      render: (value) => value ?? 0,
    },
    {
      title: "User Type",
      dataIndex: "userType",
      key: "userType",
      render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : "Guest",
    },
    {
      title: "Bot",
      dataIndex: "isBot",
      key: "isBot",
      render: (value) => (
        <Tag color={value ? "volcano" : "green"}>{value ? "Bot" : "Human"}</Tag>
      ),
    },
  ];

  const pageTitle = "Search Analytics";

  return (
    <div className="home search-analytics-page">
      <Sidebar />
      <div className="homeContainer">
        <Navbar />
        <div className="search-analytics-container">
          <div className="search-analytics-header">
            <Title level={3} style={{ margin: 0 }}>{pageTitle}</Title>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} loading={exportLoading}>
              Export CSV
            </Button>
          </div>

          <Card className="search-analytics-filters">
            <div className="search-analytics-filters-grid">
              <div>
                <Text strong>Product</Text>
                <Select
                  value={filters.product}
                  style={{ width: "100%" }}
                  onChange={(value) => handleFilterChange("product", value)}
                >
                  {PRODUCT_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>{option.label}</Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong>User Type</Text>
                <Select
                  value={filters.userType}
                  style={{ width: "100%" }}
                  onChange={(value) => handleFilterChange("userType", value)}
                >
                  {USER_TYPE_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>{option.label}</Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong>Date Range</Text>
                <Select
                  value={filters.datePreset}
                  style={{ width: "100%" }}
                  onChange={handleDatePresetChange}
                >
                  {DATE_PRESET_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>{option.label}</Option>
                  ))}
                </Select>
              </div>

              {filters.datePreset === "custom" && (
                <div>
                  <Text strong>Custom Range</Text>
                  <RangePicker
                    value={customRange}
                    style={{ width: "100%" }}
                    onChange={handleCustomRangeChange}
                  />
                </div>
              )}

              <div>
                <Text strong>Search</Text>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Search flights, hotels, events..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", minHeight: 56 }}>
                <Button type="default" onClick={() => dispatch(resetFilters())}>
                  Reset filters
                </Button>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={filters.includeBots}
                  onChange={(e) => handleFilterChange("includeBots", e.target.checked)}
                />
                <Text>Include bot traffic</Text>
              </label>
            </div>
          </Card>

          <Tabs
            activeKey={activeTab}
            onChange={(key) => dispatch(setActiveTab(key))}
            type="card"
            style={{ marginTop: 16 }}
          >
            <TabPane tab="Aggregated Searches" key="aggregated">
              <div className="search-analytics-toolbar">
                <Text type="secondary">{aggregated.pagination?.total ?? 0} matching results</Text>
              </div>

              <Table
                className="search-analytics-table"
                columns={aggregatedColumns}
                dataSource={aggregated.data}
                rowKey={(record, index) => `${record.product || "unknown"}-${index}`}
                loading={aggregated.loading}
                pagination={
                  aggregated.pagination && {
                    current: Number(aggregated.pagination.page || 1),
                    pageSize: Number(aggregated.pagination.pageSize || pageSize),
                    total: Number(aggregated.pagination.total || 0),
                    onChange: (nextPage) => dispatch(setPage(nextPage)),
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50],
                    onShowSizeChange: (_, size) => dispatch(setPage(1)),
                  }
                }
                onChange={(pagination, filters, sorter) => {
                  if (sorter && sorter.field) {
                    handleSort(sorter.field, sorter.order === "descend" ? "desc" : "asc");
                  }
                }}
                scroll={{ x: true }}
                onRow={(record) => ({
                  onClick: () => setSelectedRow(record),
                })}
              />
            </TabPane>

            <TabPane tab="Raw Search Log" key="raw">
              <div className="search-analytics-toolbar">
                <Text type="secondary">{raw.pagination?.total ?? 0} matching results</Text>
              </div>

              <Table
                className="search-analytics-table"
                columns={rawColumns}
                dataSource={raw.data}
                rowKey={(record, index) => `${record.createdAt || "raw"}-${index}`}
                loading={raw.loading}
                pagination={
                  raw.pagination && {
                    current: Number(raw.pagination.page || 1),
                    pageSize: Number(raw.pagination.pageSize || pageSize),
                    total: Number(raw.pagination.total || 0),
                    onChange: (nextPage) => dispatch(setPage(nextPage)),
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50],
                    onShowSizeChange: (_, size) => dispatch(setPage(1)),
                  }
                }
                onChange={(pagination, filters, sorter) => {
                  if (sorter && sorter.field) {
                    handleSort(sorter.field, sorter.order === "descend" ? "desc" : "asc");
                  }
                }}
                scroll={{ x: true }}
                onRow={(record) => ({
                  onClick: () => setSelectedRow(record),
                })}
              />
            </TabPane>
          </Tabs>
        </div>
      </div>

      <SheetDrawer
        title={"Search details"}
        placement="right"
        open={Boolean(selectedRow)}
        onClose={() => setSelectedRow(null)}
        width={520}
      >
        {selectedRow && (
          <div className="search-analytics-detail">
            <div className="detail-grid">
              <div className="detail-item">
                <Text strong>Product</Text>
                <div>{selectedRow.product || "Unknown"}</div>
              </div>
              <div className="detail-item">
                <Text strong>Search</Text>
                <div>{formatSearchText(selectedRow)}</div>
              </div>
              <div className="detail-item">
                <Text strong>Occurrences</Text>
                <div>{selectedRow.occurrenceCount ?? selectedRow.resultsCount ?? 0}</div>
              </div>
              <div className="detail-item">
                <Text strong>Zero Results</Text>
                <div>{selectedRow.zeroResultCount ?? (selectedRow.resultsCount === 0 ? 1 : 0)}</div>
              </div>
              <div className="detail-item">
                <Text strong>Guest</Text>
                <div>{selectedRow.guestCount ?? 0}</div>
              </div>
              <div className="detail-item">
                <Text strong>Authenticated</Text>
                <div>{selectedRow.authenticatedCount ?? 0}</div>
              </div>
              <div className="detail-item">
                <Text strong>First Searched</Text>
                <div>{selectedRow.firstSearchedAt ? moment(selectedRow.firstSearchedAt).format("DD MMM YYYY, HH:mm") : "-"}</div>
              </div>
              <div className="detail-item">
                <Text strong>Last Searched</Text>
                <div>{selectedRow.lastSearchedAt ? moment(selectedRow.lastSearchedAt).format("DD MMM YYYY, HH:mm") : "-"}</div>
              </div>
              <div className="detail-item">
                <Text strong>User Type</Text>
                <div>{selectedRow.userType || (admin ? "Authenticated" : "Guest")}</div>
              </div>
              <div className="detail-item">
                <Text strong>Bot</Text>
                <div>{selectedRow.isBot ? "Bot" : "Human"}</div>
              </div>
            </div>
          </div>
        )}
      </SheetDrawer>
    </div>
  );
};

export default SearchAnalytics;
